import React, { useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Chip,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { BackArrowIcon, HighDemandIcon, TopRatingIcon, TrendingIcon } from '../icons';
import { GlobalStyles } from '@mui/material';
import ThreeDotMenu from '../components/university-table/ThreeDotMenu';
import UniversitySummary from '../components/university-table/UniversitySummary';
import { fetchSpecializationsByNameId } from '../api/specializationApi'
import { updateStatus } from '../api/adminApi';
import CommonTableFormat from '../components/common/CommonTableFormat';
import CommonFilterTab from '../components/common/CommonFilterTab';
import CommonDataTable from '../components/common/CommonDataTable';
import { formatMultiQueryParams, jsonParserFormatter } from '../utils/helperFunction';
import { fetchUniversitiesNames } from '../api/universityApi';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { defaultPaginationValues, filterTypeOptions, studentStatusList } from '../utils/constant';
import { setFilterField } from '../containers/filter-container/reducer/filterSlice';
import { fetchCourseNameList } from '../api/courseApi';

const tagOptions = [
  { label: 'Trending', icon: <TrendingIcon fontSize="small" /> },
  { label: 'Top Rated', icon: <TopRatingIcon fontSize="small" /> },
  { label: 'High Demand', icon: <HighDemandIcon fontSize="small" /> },
];

const courseCategory = [
  { label: 'Management', },
  { label: 'Technology', },
  { label: 'Science', },
  { label: 'Commerce', },
  { label: 'Health & Medicine', },
  { label: 'Arts & Humanities', },
];

const ParentsSpecializationListingManagement = ({ isHandlBtn = false }) => {
  const dispatch = useDispatch()
  const filterSlice = useSelector(state => state.filter?.filters)
  const LOCATION_FILTER = `filters:${window.location.pathname}`
  const [rows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [searchText, setSearchText] = React.useState('');
  const [selectedTag, setSelectedTag] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const navigate = useNavigate();
  const { id: nameId } = useParams();
  const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 50, });
  const [rowCount, setRowCount] = React.useState(0);
  const [searchFilter, setSearchFilter] = React.useState({})
  const [pageFilters, setPageFilters] = React.useState(defaultPaginationValues);
  const [isRender, setIsRender] = React.useState(false)
  const [universitySuggestion, setUniversitySuggesion] = React.useState([])
  const [courseSuggestion, setCourseSuggestion] = React.useState([])



  const handleFliterSliceChange = (values = {}, isReset = false) => {
    let isQuery = typeof values === "object" && Object.keys(values)?.length
    isQuery && dispatch(setFilterField({ pathname: LOCATION_FILTER, filters: JSON.stringify({ ...(isReset ? {} : pageFilters), ...defaultPaginationValues, ...values }) }))
  }

  const fetchDetails = () => {
    if (!nameId) return;
    setLoading(true);
    const multiFilter = formatMultiQueryParams(filterSlice[LOCATION_FILTER])
    fetchSpecializationsByNameId(nameId, setRows, { ...searchFilter, ...multiFilter, isMultiFilter: true, }, setRowCount)
      .catch((err) => {
        console.error('fetchSpecializationsByNameId failed:', err);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }

  const handleChange = async (id, field, value) => {
    if (field === 'action') {
      if (value === 'View') {
        const specializationId = rows.find(r => r.id === id)?.specId || '';
        navigate(`/specialization-listing-management/specialization-details/${specializationId}`, {
          state: { viewOnly: true, source: 'ActionMenu' }
        });
        return;
      }

      if (value === 'Send to draft') {
        const prevStatus = rows.find(r => r.id === id)?.status || '';
        setRows(prev => prev.map(r => r.id === id ? { ...r, status: 'Draft' } : r));
        try {
          await updateStatus({
            id,
            status: 'draft',
            type: 'specialization',
          });
        } catch (err) {
          setRows(prev => prev.map(r => r.id === id ? { ...r, status: prevStatus } : r));
        }
        return;
      }
      return;
    }
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };


  function getStatusClass(status) {
    switch (status) {
      case 'Live':
        return 'status-live';
      case 'Draft':
        return 'status-draft';
      case 'Pending':
        return 'status-pending';
      case 'Deactivated':
        return 'status-deactivated';
      default:
        return '';
    }
  }

  const columns = [
    {
      field: 'sNo',
      headerName: 'S. No.',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // renderCell: (params) => filteredRows.findIndex((r) => r.id === params.row.id) + 1,
    },
    {
      field: 'specId',
      headerName: 'Spec. ID',
      flex: 1,
      minWidth: 130,
      cellClassName: 'sticky-col-1',
      headerClassName: 'sticky-col-1',
      filterColumnName: "specialisationId",
      filterType: filterTypeOptions.string,

    },
    {
      field: 'name',
      headerName: 'Specialization Name',
      flex: 2,
      minWidth: 280,
      filterable: false,
      renderCell: (params) => {
        const { img, name, specId } = params.row;

        const getInitials = (fullName) => {
          if (!fullName) return '';
          const words = fullName.trim().split(' ').filter(Boolean);
          if (words.length === 1) return words[0][0]?.toUpperCase() || '';
          if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
          return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
        };

        const stringToColor = (str) => {
          let hash = 0;
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
          }
          const c = (hash & 0x00ffffff).toString(16).toUpperCase();
          return '#' + '00000'.substring(0, 6 - c.length) + c;
        };

        const bgColor = stringToColor(name || 'Course');

        return loading ? (
          <Skeleton width={150} />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(`/specialization-listing-management/specialization-details/${specId}`)}
          >
            {img ? (
              <Box
                component="img"
                src={img}
                alt="logo"
                sx={{
                  width: 40,
                  height: 40,
                  padding: '5px',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                  display: 'block',
                  maxHeight: 60,
                }}
              />
            ) : (
              <Box
                sx={{
                  minWidth: 35,
                  minHeight: 35,
                  maxWidth: 35,
                  maxHeight: 35,
                  borderRadius: '50%',
                  backgroundColor: bgColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                {getInitials(name)}
              </Box>
            )}
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'courseName',
      headerName: 'Course Name',
      flex: 1,
      minWidth: 160,
      filterColumnName: "courseNameId.name",
      filterType: filterTypeOptions.selection,
      selection: courseSuggestion,
    },
    {
      field: 'university',
      headerName: 'University',
      flex: 2,
      minWidth: 260,
      filterColumnName: "universityId.universityName",
      filterType: filterTypeOptions.selection,
      selection: universitySuggestion,
      renderCell: (params) => {
        const { universityLogo, university, universityId } = params.row;
        const displayName = university;

        const getInitials = (fullName) => {
          if (!fullName) return '';
          const words = fullName.trim().split(' ').filter(Boolean);
          if (words.length === 1) return words[0][0]?.toUpperCase() || '';
          if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase();
          return (words[0][0] + words[1][0] + words[2][0]).toUpperCase();
        };

        const stringToColor = (str) => {
          let hash = 0;
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
          }
          const c = (hash & 0x00ffffff).toString(16).toUpperCase();
          return '#' + '00000'.substring(0, 6 - c.length) + c;
        };

        const bgColor = stringToColor(displayName || 'University');

        return loading ? (
          <Skeleton width={150} />
        ) : (
          <Box display="flex" alignItems="center" gap={1} sx={{ height: '100%' }}>
            {universityLogo ? (
              <Box
                component="img"
                src={universityLogo}
                alt="universityLogo"
                sx={{
                  width: 40,
                  height: 40,
                  padding: '5px',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                  display: 'block',
                  maxHeight: 60,
                }}
              />
            ) : (
              <Box
                sx={{
                  minWidth: 35,
                  minHeight: 35,
                  maxWidth: 35,
                  maxHeight: 35,
                  borderRadius: '50%',
                  backgroundColor: bgColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                {getInitials(displayName)}
              </Box>
            )}
            <Box>
              <Typography sx={{ fontSize: '13px !important', fontWeight: 500 }}>
                {displayName}
              </Typography>
              {params.row.universityNote && (
                <Typography sx={{ fontSize: '11px !important', fontWeight: '500', color: '#363853' }}>
                  ({params.row.universityNote})
                </Typography>
              )}
              <Typography
                sx={{ fontSize: '11px !important', fontWeight: '500', color: '#6E7485', paddingTop: '4px' }}
              >
                University ID: {universityId}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 130,
      filterColumnName: "status",
      filterType: filterTypeOptions.selection,
      selection: studentStatusList,
      renderCell: (params) =>
        loading ? (
          <Skeleton width={70} />
        ) : (
          <Chip
            label={params.value}
            size="small"
            icon={
              <FiberManualRecordIcon
                className={getStatusClass(params.value)}
                sx={{
                  fontSize: 10,
                  color: getStatusColor(params.value),
                }}
              />
            }
            sx={{
              bgcolor: getStatusBgColor(params.value),
              color: getStatusTextColor(params.value),
              fontWeight: 500,
            }}
          />
        ),
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 130,
      filterColumnName: "createdAt",
      filterType: filterTypeOptions.dateRange,
    },
    {
      field: 'lastUpdate',
      headerName: 'Last Update',
      flex: 1,
      minWidth: 130,
      filterColumnName: "updatedAt",
      filterType: filterTypeOptions.dateRange,
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 100,
      filterable: false,
      renderCell: (params) =>
        loading ? <Skeleton width={80} /> : <ThreeDotMenu rowId={params.row.id} onAction={handleChange} />,
    },
  ];


  function getStatusBgColor(status) {
    switch (status) {
      case 'Live':
        return '#fff';
      case 'Draft':
        return '#fff';
      case 'Pending':
        return '#fff';
      case 'Deactivated':
        return '#fff';
      default:
        return '#fff';
    }
  }

  function getStatusTextColor(status) {
    switch (status) {
      case 'Live':
        return '#363853'; // darker green
      case 'Draft':
        return '#363853'; // darker gray
      case 'Pending':
        return '#363853'; // darker orange
      case 'Deactivated':
        return '#363853'; // darker red
      default:
        return '#666';
    }
  }

  // Helper functions for status colors
  function getStatusColor(status) {
    switch (status) {
      case 'Live':
        return '#22c55e'; // green
      case 'Draft':
        return '#64748b'; // grayish
      case 'Pending':
        return '#f97316'; // orange
      case 'Deactivated':
        return '#ef4444'; // red
      default:
        return '#999';
    }
  }


  // initial load or when filter slice change
  React.useEffect(() => {
    fetchDetails()
  }, [filterSlice[LOCATION_FILTER], isRender, nameId])

  // handle extra filter tabs changes
  React.useEffect(() => {
    if (pageFilters?.page) {
      handleFliterSliceChange({ page: 0 })
    } else {
      setIsRender(!isRender)
    }
  }, [searchFilter])

  // parse page filters
  React.useEffect(() => {
    const values = jsonParserFormatter(filterSlice[LOCATION_FILTER])
    setPageFilters(values)
  }, [filterSlice[LOCATION_FILTER]])

  // load course/ universities for filter suggestions
  React.useEffect(() => {
    fetchUniversitiesNames((result) => setUniversitySuggesion(result?.map(item => ({ ...item, value: encodeURIComponent(item?.label) }))))
      .catch(err => console.log("error:---", err))

    fetchCourseNameList((result) => { setCourseSuggestion(result?.map(el => ({ label: el?.course, value: el?.course }))) }, { skipLimit: true })
      .catch(() => { })
  }, [])

  return (
    <>
      {/* Apply global styles to DataGrid dropdown menus */}
      {/* <GlobalStyles
        styles={{
          '.MuiDataGrid-menuList': {
            fontSize: '13px',
            padding: '4px 0',
          },
          '.MuiDataGrid-panelContent': {
            fontSize: '13px',
            padding: '4px 0',
          },
          '.MuiSelect-select': {
            fontSize: '13px',
            padding: '4px 0',
          },
          '.MuiList-root .MuiButtonBase-root': {
            fontSize: '13px',
          },
          '.MuiDataGrid-panelContent .MuiOutlinedInput-input': {
            fontSize: '13px',
          },
          '.MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label': {
            fontSize: '13px',
          },
          '.MuiDataGrid-menuList .MuiTypography-root': {
            fontSize: '13px',
          },
          '.MuiDataGrid-menuList .MuiMenuItem-root': {
            fontSize: '13px',
            padding: '6px 12px',
          },
          '.MuiDataGrid-menuList .MuiMenuItem-root:hover': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-columnHeaders': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: '#fff',
          },
          '& .MuiDataGrid-cell.sticky-col-1': {
            position: 'sticky',
            left: 0,
            background: '#fff',
            zIndex: 2,
          },
          '& .MuiDataGrid-columnHeader.sticky-col-1': {
            position: 'sticky',
            left: 0,
            background: '#fff',
            zIndex: 3, // slightly higher than cell
          },
        }}
      /> */}

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div className="page-header" style={{ position: 'sticky', top: '4.5rem', zIndex: '9', padding: '10px 15px', borderBottom: '1px solid #E6E7E8', background: '#fff', }}>
          <div className="row align-items-center">
            <div className="col">
              <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#15243E', display: 'flex', alignItems: 'center', gap: 1, }}><Link href="/specialization-listing-management" className='d-flex'><BackArrowIcon /></Link>Parents Specialization Listing Management  </Typography>
            </div>
            {/* End Col */}
            {isHandlBtn && (<div className="col">  <Box display="flex" gap={2} sx={{ justifyContent: 'end', }}>
              <Button onClick={() => navigate('/specialization-listing-management/specialization-details')} sx={{ background: '#0463EF', color: '#fff', minHeight: 'auto', padding: '4px 15px', fontSize: '14px', textTransform: 'capitalize', }}>
                Add Parents Specialization
              </Button>
            </Box></div>)}
          </div>
          {/* End Col */}
        </div>
        <UniversitySummary type="specialization" isAdmin />
        {/* Filter Toolbar */}
        <CommonFilterTab type='specialization' searchPlaceholder='Search course name...' detailType='Specialization Details' fetchList={setSearchFilter} showCount={rows?.length} rowCount={rowCount} />

        {/* Data Table */}
        <CommonDataTable columns={columns} rows={rows} idKey="field" loading={loading} filterEnabled={true} rowCount={rowCount}
          pageFilters={pageFilters}
          handleFliterSliceChange={handleFliterSliceChange}
        />

      </Box>
    </>
  )
}

export default ParentsSpecializationListingManagement
